/**
 * Export Utilities for NurinV2
 *
 * CSV/Excel export functionality for patient data.
 * Optimized for WhatsApp Business API bulk messaging.
 */

import { normalizePhoneForWhatsApp } from './whatsapp'

export interface ExportPatient {
  id: number
  name: string
  phone: string | null
  branchName: string | null
  pno: string
  nric?: string | null
  email?: string | null
  totalVisits?: number | null
  totalSpent?: string | null
  lastVisitDate?: string | null
  waBlastCount?: number
  waLastSentAt?: string | null
  waLastCampaignName?: string | null
  waLastStatus?: string | null
  waEverReplied?: boolean
  waOptedOut?: boolean
}

export interface ExportOptions {
  /** Include only patients with valid phone numbers */
  validPhoneOnly?: boolean
  /** Export format */
  format?: 'basic' | 'full'
  /** Custom filename (without extension) */
  filename?: string
}

/**
 * Convert array of objects to CSV string
 */
export function arrayToCSV<T extends Record<string, unknown>>(
  data: T[],
  columns: { key: keyof T; header: string }[]
): string {
  // Header row
  const headers = columns.map((c) => `"${c.header}"`).join(',')

  // Data rows
  const rows = data.map((row) =>
    columns
      .map((c) => {
        const value = row[c.key]
        if (value === null || value === undefined) return '""'
        // Escape quotes and wrap in quotes
        const str = String(value).replace(/"/g, '""')
        return `"${str}"`
      })
      .join(',')
  )

  return [headers, ...rows].join('\n')
}

/**
 * Trigger browser download of a file
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Clean up
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

/**
 * Export patients to CSV for WhatsApp Business API
 *
 * Basic format: phone, name (for WhatsApp API)
 * Full format: phone, name, branch, pno, visits, spent, last_visit
 */
export function exportPatientsToCSV(
  patients: ExportPatient[],
  options: ExportOptions = {}
): { success: boolean; count: number; filename: string } {
  const { validPhoneOnly = true, format = 'basic', filename } = options

  // Filter and transform data
  let exportData = patients.map((p) => ({
    ...p,
    phoneNormalized: normalizePhoneForWhatsApp(p.phone),
    lastVisitFormatted: p.lastVisitDate
      ? new Date(p.lastVisitDate).toLocaleDateString('en-MY')
      : '',
    totalSpentFormatted: p.totalSpent
      ? `RM ${parseFloat(p.totalSpent).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`
      : '',
    waBlasts: p.waBlastCount ?? 0,
    waLastCampaign: p.waLastCampaignName ?? '',
    waLastSentFormatted: p.waLastSentAt ? new Date(p.waLastSentAt).toLocaleDateString('en-MY') : '',
    waStatus: p.waOptedOut
      ? 'opted_out'
      : p.waEverReplied
        ? 'replied'
        : p.waLastStatus ?? (p.waBlastCount ? '' : 'never'),
  }))

  // Filter out invalid phones if requested
  if (validPhoneOnly) {
    exportData = exportData.filter((p) => p.phoneNormalized !== null)
  }

  if (exportData.length === 0) {
    return { success: false, count: 0, filename: '' }
  }

  // Define columns based on format
  const columns =
    format === 'basic'
      ? [
          { key: 'phoneNormalized' as const, header: 'phone' },
          { key: 'name' as const, header: 'name' },
        ]
      : [
          { key: 'phoneNormalized' as const, header: 'phone' },
          { key: 'name' as const, header: 'name' },
          { key: 'branchName' as const, header: 'branch' },
          { key: 'pno' as const, header: 'pno' },
          { key: 'totalVisits' as const, header: 'visits' },
          { key: 'totalSpentFormatted' as const, header: 'total_spent' },
          { key: 'lastVisitFormatted' as const, header: 'last_visit' },
          { key: 'waBlasts' as const, header: 'wa_blasts' },
          { key: 'waLastCampaign' as const, header: 'wa_last_campaign' },
          { key: 'waLastSentFormatted' as const, header: 'wa_last_sent' },
          { key: 'waStatus' as const, header: 'wa_status' },
        ]

  const csv = arrayToCSV(exportData, columns)

  // Generate filename
  const date = new Date().toISOString().split('T')[0]
  const exportFilename = filename || `patients-whatsapp-${date}`

  downloadFile(csv, `${exportFilename}.csv`, 'text/csv;charset=utf-8;')

  return {
    success: true,
    count: exportData.length,
    filename: `${exportFilename}.csv`,
  }
}

/**
 * Export patients for WhatsApp with custom message template
 * Creates a CSV with phone, name, and pre-filled message
 */
export function exportPatientsWithMessage(
  patients: ExportPatient[],
  messageTemplate: string,
  options: ExportOptions = {}
): { success: boolean; count: number; filename: string } {
  const { validPhoneOnly = true, filename } = options

  // Filter and transform data
  let exportData = patients
    .map((p) => {
      const phoneNormalized = normalizePhoneForWhatsApp(p.phone)
      const message = messageTemplate
        .replace(/{name}/g, p.name || '')
        .replace(/{branch}/g, p.branchName || '')

      return {
        phone: phoneNormalized,
        name: p.name,
        branch: p.branchName,
        message,
      }
    })
    .filter((p) => !validPhoneOnly || p.phone !== null)

  if (exportData.length === 0) {
    return { success: false, count: 0, filename: '' }
  }

  const columns = [
    { key: 'phone' as const, header: 'phone' },
    { key: 'name' as const, header: 'name' },
    { key: 'branch' as const, header: 'branch' },
    { key: 'message' as const, header: 'message' },
  ]

  const csv = arrayToCSV(exportData, columns)

  const date = new Date().toISOString().split('T')[0]
  const exportFilename = filename || `patients-whatsapp-blast-${date}`

  downloadFile(csv, `${exportFilename}.csv`, 'text/csv;charset=utf-8;')

  return {
    success: true,
    count: exportData.length,
    filename: `${exportFilename}.csv`,
  }
}

/**
 * Get export statistics without actually exporting
 */
export function getExportStats(patients: ExportPatient[]): {
  total: number
  withValidPhone: number
  withoutPhone: number
} {
  const withValidPhone = patients.filter(
    (p) => normalizePhoneForWhatsApp(p.phone) !== null
  ).length

  return {
    total: patients.length,
    withValidPhone,
    withoutPhone: patients.length - withValidPhone,
  }
}

// ============================================================================
// Revenue Export
// ============================================================================

export interface MonthlyRevenueExport {
  label: string
  revenue: number
  invoices: number
  patients: number
  avgTransaction: number
  growth: number | null
}

/**
 * Export monthly revenue comparison to CSV
 */
export function exportRevenueToCSV(
  data: MonthlyRevenueExport[],
  options?: { filename?: string }
): { success: boolean; count: number; filename: string } {
  if (data.length === 0) {
    return { success: false, count: 0, filename: '' }
  }

  const columns = [
    { key: 'label' as const, header: 'Month' },
    { key: 'revenue' as const, header: 'Revenue (RM)' },
    { key: 'invoices' as const, header: 'Invoices' },
    { key: 'patients' as const, header: 'Patients' },
    { key: 'avgTransaction' as const, header: 'Avg Transaction (RM)' },
    { key: 'growth' as const, header: 'Growth (%)' },
  ]

  // Format numbers for export
  const exportData = data.map((row) => ({
    label: row.label,
    revenue: row.revenue.toFixed(2),
    invoices: row.invoices.toString(),
    patients: row.patients.toString(),
    avgTransaction: row.avgTransaction.toFixed(2),
    growth: row.growth !== null ? row.growth.toString() : '-',
  }))

  const csv = arrayToCSV(exportData, columns)

  const date = new Date().toISOString().split('T')[0]
  const exportFilename = options?.filename || `revenue-report-${date}`

  downloadFile(csv, `${exportFilename}.csv`, 'text/csv;charset=utf-8;')

  return {
    success: true,
    count: data.length,
    filename: `${exportFilename}.csv`,
  }
}

// ============================================================================
// Report Export Utilities
// ============================================================================

export interface ReportExportOptions {
  filename?: string
  format: 'csv' | 'pdf'
}

/**
 * Export generic data to CSV
 */
export function exportDataToCSV<T extends Record<string, unknown>>(
  data: T[],
  columns: { key: keyof T; header: string }[],
  options?: { filename?: string }
): { success: boolean; count: number; filename: string } {
  if (data.length === 0) {
    return { success: false, count: 0, filename: '' }
  }

  const csv = arrayToCSV(data, columns)

  const date = new Date().toISOString().split('T')[0]
  const exportFilename = options?.filename || `report-${date}`

  downloadFile(csv, `${exportFilename}.csv`, 'text/csv;charset=utf-8;')

  return {
    success: true,
    count: data.length,
    filename: `${exportFilename}.csv`,
  }
}

/**
 * Generate printable HTML for PDF export
 * Uses browser print dialog for PDF generation
 */
export function printReportAsPDF(
  title: string,
  content: string,
  options?: {
    subtitle?: string
    branchName?: string
    period?: { startDate: string; endDate: string }
  }
): void {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    alert('Please allow popups for this website to export PDF')
    return
  }

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          padding: 40px;
          color: #333;
          line-height: 1.6;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #e5e7eb;
        }
        .logo {
          font-size: 24px;
          font-weight: 700;
          color: #d4a574;
          margin-bottom: 8px;
        }
        .title {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
        }
        .subtitle {
          font-size: 14px;
          color: #6b7280;
        }
        .meta {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #6b7280;
          margin-top: 12px;
        }
        .content {
          margin-top: 20px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
        }
        th, td {
          padding: 10px 12px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }
        th {
          background-color: #f9fafb;
          font-weight: 600;
          font-size: 12px;
          text-transform: uppercase;
          color: #6b7280;
        }
        td {
          font-size: 13px;
        }
        .text-right {
          text-align: right;
        }
        .text-success {
          color: #10b981;
        }
        .text-danger {
          color: #ef4444;
        }
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        .summary-card {
          background: #f9fafb;
          padding: 16px;
          border-radius: 8px;
        }
        .summary-card .value {
          font-size: 20px;
          font-weight: 700;
          color: #1f2937;
        }
        .summary-card .label {
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
        }
        .section-title {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 24px 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
          font-size: 11px;
          color: #9ca3af;
          text-align: center;
        }
        @media print {
          body { padding: 20px; }
          @page { margin: 1cm; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">Klinik Amisi</div>
        <div class="title">${title}</div>
        ${options?.subtitle ? `<div class="subtitle">${options.subtitle}</div>` : ''}
        <div class="meta">
          ${options?.branchName ? `<span>Branch: ${options.branchName}</span>` : '<span></span>'}
          ${options?.period ? `<span>Period: ${formatDate(options.period.startDate)} - ${formatDate(options.period.endDate)}</span>` : ''}
        </div>
      </div>
      <div class="content">
        ${content}
      </div>
      <div class="footer">
        Generated by NurinV2 on ${new Date().toLocaleString('en-MY')}
      </div>
    </body>
    </html>
  `

  printWindow.document.write(html)
  printWindow.document.close()

  // Wait for content to load then print
  printWindow.onload = () => {
    printWindow.print()
  }
}

/**
 * Format currency for display/export
 */
export function formatCurrencyForExport(value: number): string {
  return `RM ${value.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Generate HTML table from data
 */
export function generateHTMLTable<T extends Record<string, unknown>>(
  data: T[],
  columns: { key: keyof T; header: string; align?: 'left' | 'right'; format?: (value: unknown) => string }[]
): string {
  if (data.length === 0) return '<p>No data available</p>'

  const headers = columns.map(c => `<th class="${c.align === 'right' ? 'text-right' : ''}">${c.header}</th>`).join('')

  const rows = data.map(row => {
    const cells = columns.map(c => {
      const value = row[c.key]
      const formatted = c.format ? c.format(value) : String(value ?? '-')
      return `<td class="${c.align === 'right' ? 'text-right' : ''}">${formatted}</td>`
    }).join('')
    return `<tr>${cells}</tr>`
  }).join('')

  return `
    <table>
      <thead><tr>${headers}</tr></thead>
      <tbody>${rows}</tbody>
    </table>
  `
}

/**
 * Generate summary cards HTML
 */
export function generateSummaryCardsHTML(
  cards: { label: string; value: string }[]
): string {
  const cardElements = cards.map(card => `
    <div class="summary-card">
      <div class="value">${card.value}</div>
      <div class="label">${card.label}</div>
    </div>
  `).join('')

  return `<div class="summary-grid">${cardElements}</div>`
}
